(ns admin.containers.activity.advanced
  (:require [re-frame.core :as re-frame]
            [reagent.core :as reagent]

            [kyber.components.base.text :as text]
            [kyber.components.base.icons :as icon]
            [kyber.styles.core :refer [gs styles->classes]]
            [kyber.components.base.button :as button]
            [kyber.components.base.link :as link]
            [kyber.utils :as ku]
            [kyber.components.composite.loading :as loading]

            [admin.components.forms.looker-dashboard :as looker-dashboard]
            [admin.components.forms.integration :as integration-form]))

(def classes
  (styles->classes
   {:container
    {:margin [(gs [:spacing :p40]) :auto]
     :max-width "1000px"
     :padding (gs [:spacing :p12])}

    :section-container
    {:margin [(gs [:spacing :p40])
              (gs [:spacing :p0])]}

    :integration-card-container
    {:padding [(gs [:spacing :p12])
               (gs [:spacing :p20])]
     :background-color (gs [:colors :true-white])
     :border-radius (gs [:radius :r3])
     :border (str "1px solid " (gs [:colors :gray-scale-100]))
     :margin-bottom (gs [:spacing :p20])}

    :idp-title-container
    {:display :flex
     :align-items :center

     "& > *:first-child"
     {:margin-right (gs [:spacing :p20])}}

    :idp-title
    {:margin-bottom (gs [:spacing :p20])}

    :looker-dashboard-item
    {:margin-top (gs [:spacing :p20])
     :display :flex
     :align-items :center
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))}

    :looker-dashboard-item-options
    {:display :flex
     :flex-direction :row
     :justify-content :center
     :align-items :center
     :margin-left (gs [:spacing :p8])}

    :looker-dashboard-item-option-link
    {:margin-left (gs [:spacing :p4])}

    :url-container
    {:border-left (str "4px solid " (gs [:colors :gray-scale-200]))
     :padding-left (gs [:spacing :p20])
     :margin-bottom (gs [:spacing :p20])}

    :dashboard-modifications-container
    {:display :flex
     :flex-direction :row}

    :no-integrations-message-container
    {:padding (gs [:spacing :p20])
     :background-color (gs [:colors :true-white])

     "& > *"
     {:text-align :center}}}))

(defn- jedha-sso-link
  [{:keys [activity-uuid curriculum-uuid identity-provider-uuid]}]
  (str "/api/v1/curricula/" curriculum-uuid "/activities/" activity-uuid
       "/identity-providers/" identity-provider-uuid "/sso"))

(defn- saml-sso-link
  [{:keys [activity-uuid curriculum-uuid identity-provider-uuid]}]
  (str "/api/v1/learner/curricula/" curriculum-uuid "/activities/" activity-uuid
       "/authorize?identity-provider=" identity-provider-uuid))

(defn- render-no-integrations-view
  []
  [:div {:class (:no-integrations-message-container classes)}
   [text/title-30 :p "No Integrations Configured."]])

(defn- render-url
  [{:keys [label url]}]
  [:div {:class [(:url-container classes)]}
   [text/caption-30 :p label]
   [text/caption-20 :p url]])

(defn- render-disabled-passwordless
  []
  [:div
   [:div {:style {:display "flex" :flex-direction "row" :align-items "center"}}
    [icon/icon-sm {:style {:color        (gs [:colors :gray-scale-500])
                           :margin-right (gs [:spacing :p4])}}
     :exclamation-circle]
    [text/caption-30 :p "Passwordless Login Disabled"]]
   [text/caption-20 :p "No learners will be able to access this activity without using SAML or Jedha SSO."]])

(defn- render-integration-card
  [_]
  (let [is-editing (reagent/atom false)]
    (fn [{:keys [integration identity-provider activity-uuid curriculum-uuid]}]
      (let [{:keys [homepage-url
                    post-test-postback-url credit-url
                    disable-passwordless-sign-in
                    xapi-url]}
            integration
            {:keys [is-saml]} identity-provider]
        [:li {:class [(:integration-card-container classes)]}
         (if (true? @is-editing)
           [integration-form/render
            (merge
             integration
             {:available-identity-providers [identity-provider]
              :identity-provider-name (:name identity-provider)
              :activity-uuid activity-uuid
              :curriculum-uuid curriculum-uuid})
            #(reset! is-editing false)]
           [:<>
            [:div {:class [(:idp-title-container classes)]}
             [text/title-30 :p
              {:class [(:idp-title classes)]}
              (:name identity-provider)]

             [button/button-link {:on-click #(reset! is-editing true)} "Edit"]]

            [link/primary-anchor-button
             {:href (cond-> {:activity-uuid activity-uuid
                             :curriculum-uuid curriculum-uuid
                             :identity-provider-uuid (:uuid identity-provider)}

                      is-saml saml-sso-link
                      (not is-saml) jedha-sso-link)}

             (if is-saml "SAML SSO" "Jedha SSO")]

            [:div
             [render-url {:label "Homepage URL"
                          :url homepage-url}]
             [render-url {:label "Credit URL"
                          :url credit-url}]

             (if is-saml
               [:<>
                [render-url {:label "xAPI URL"
                             :url xapi-url}]
                [render-url {:label "xAPI Token"
                             :url "*********"}]]

               [render-url {:label "Post Test Post Back URL"
                            :url post-test-postback-url}])
             (when (boolean disable-passwordless-sign-in)
               [render-disabled-passwordless])]])]))))

(defn- render-integrations-list
  [{:keys [identity-providers integrations activity-uuid curriculum-uuid]}]
  [:ul
   (for [{:keys [identity-provider-uuid] :as integration} integrations]
     ^{:key identity-provider-uuid}
     [render-integration-card
      {:integration integration
       :identity-provider (get identity-providers identity-provider-uuid)
       :activity-uuid activity-uuid
       :curriculum-uuid curriculum-uuid}])])

(defn- render-looker-dashboard-form
  [{:keys [activity-uuid curriculum-uuid dashboards state close-form]}]
  [looker-dashboard/render {:activity-uuid activity-uuid
                            :close-form close-form
                            :curriculum-uuid curriculum-uuid
                            :dashboards dashboards
                            :state state}])

(defn- render-looker-dashboard-item [{:keys [name key]}]
  [:li
   {:key (str "dashboard-list-item-" key)
    :class [(:looker-dashboard-item classes)]}
   [text/title-10 :p name]])

(defn- render-looker-dashboard-list
  [dashboards]
  [:ul
   (for [{:keys [key] :as dashboard} dashboards]
     ^{:key (str "item-key-" key)}
     [render-looker-dashboard-item dashboard])])

(defn- render-view-looker-dashboards [dashboards]
  (if (pos? (count dashboards))
    [render-looker-dashboard-list dashboards]
    [text/body-20 :p "No Looker dashboards."]))

(defn render
  [{:keys [params]}]
  (let [{:keys [curriculum-uuid activity-uuid]}
        params

        adding-new-integration
        (reagent/atom false)

        manage-looker-dashboards-state
        (reagent/atom :view)

        identity-providers
        (re-frame/subscribe [:identity-providers])

        activity-integrations
        (re-frame/subscribe [:identity-providers/activity-integrations
                             {:activity-uuid activity-uuid
                              :curriculum-uuid curriculum-uuid}])

        activity (re-frame/subscribe
                  [:activities/activity {:curriculum-uuid curriculum-uuid
                                         :activity-uuid activity-uuid}])
        activity-report-download-url
        (re-frame/subscribe [:activity-report/download-url
                             {:activity-uuid activity-uuid
                              :curriculum-uuid curriculum-uuid}])]
    (fn [{:keys [resource-status]}]
      (let [available-identity-providers
            (vals
             (apply dissoc @identity-providers
                    (map :identity-provider-uuid
                         @activity-integrations)))]

        (if (ku/all-resources-loaded? resource-status)
          [:div {:class (:container classes)}
           [text/title-40 :h3
            "Advanced Settings"]

           (when (some? @activity-report-download-url)
             [:div {:class [(:section-container classes)]}
              [text/title-30 :p
               "Download Activity Report"]
              [text/body-20 :p
               (str "This report contains all learner data for this activity, "
                    "formatted in a csv file.")]

              [link/primary-anchor-button
               {:href @activity-report-download-url
                :target "_blank"}
               "Download Resource"]])

           [:div {:class [(:section-container classes)]}
            [text/title-30 :p
             "Manage Activity Integrations"]
            [text/body-20 :p
             (str "Activity integrations are used to integrate with partners "
                  "to post data or redirect learners back to their platforms "
                  "after they have completed the activity.")]

            (if (true? @adding-new-integration)
              [:div
               [integration-form/render
                {:available-identity-providers available-identity-providers
                 :activity-uuid activity-uuid
                 :curriculum-uuid curriculum-uuid}
                #(reset! adding-new-integration false)]
               [button/secondary-button
                {:on-click #(reset! adding-new-integration false)
                 :style {:margin-top (gs [:spacing :p20])}}
                "Cancel"]]
              [:div
               (when (some? available-identity-providers)
                 [button/primary-button
                  {:on-click #(reset! adding-new-integration true)
                   :style {:margin-bottom (gs [:spacing :p20])}}
                  "Add Integration"])

               (if (empty? @activity-integrations)
                 [render-no-integrations-view]
                 [render-integrations-list
                  {:identity-providers @identity-providers
                   :integrations @activity-integrations
                   :activity-uuid activity-uuid
                   :curriculum-uuid curriculum-uuid}])])]
           [:div {:class [(:section-container classes)]}
            [text/title-30 :p
             "Manage Looker Dashboards"]
            (let [dashboards (:looker @activity)]
              [:div
               (condp = @manage-looker-dashboards-state
                 :view [render-view-looker-dashboards dashboards]
                 :edit [render-looker-dashboard-form
                        {:activity-uuid activity-uuid
                         :curriculum-uuid curriculum-uuid
                         :dashboards dashboards
                         :state :edit
                         :close-form #(reset! manage-looker-dashboards-state
                                              :view)}]
                 :add [render-looker-dashboard-form
                       {:activity-uuid activity-uuid
                        :curriculum-uuid curriculum-uuid
                        :dashboards dashboards
                        :state :add
                        :close-form #(reset! manage-looker-dashboards-state
                                             :view)}])

               (when (= @manage-looker-dashboards-state :view)
                 [:div {:class [(:dashboard-modifications-container classes)]}
                  [button/primary-button
                   {:on-click #(reset! manage-looker-dashboards-state :edit)
                    :style {:margin-top (gs [:spacing :p20])}}
                   "Edit Dashboards"]
                  [button/primary-button
                   {:on-click #(reset! manage-looker-dashboards-state :add)
                    :style {:margin-left (gs [:spacing :p20])
                            :margin-top (gs [:spacing :p20])}}
                   "Add New Dashboard"]])])]]

          [loading/render
           {:loading-message "Please wait while we lookup your settings"}])))))
